import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getKingdom } from '../api/kingdom';
import type { Kingdom } from '../types/kingdom';

interface UseKingdomResult {
  kingdom: Kingdom | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetches a Kingdom by id.
 *
 * SF-169 Decision 4: hardened with the same `cancelled`-flag guard `useDossier.ts` already has —
 * this hook is no longer just Dossier's secondary nav-metadata fetch, it's the primary roster
 * source for `KingdomViewer`/`Kingdom.tsx`, so a stale out-of-order response landing after a rapid
 * id change must not clobber newer state. Restructured to the same effect-with-cleanup +
 * `refreshKey`-bump shape as `useDossier` for consistency between the two hooks.
 */
export function useKingdom(kingdomId: string | null): UseKingdomResult {
  const { worldId } = useAuth();
  const [kingdom, setKingdom] = useState<Kingdom | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!kingdomId || !worldId) {
      setKingdom(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getKingdom(worldId, kingdomId).then((result) => {
      if (cancelled) return;
      if (result.error) {
        setError(result.error);
        setKingdom(null);
      } else {
        setKingdom(result.kingdom);
      }
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [kingdomId, worldId, refreshKey]);

  return { kingdom, isLoading, error, refresh };
}
